/**
 * Component: Judge.me reviews sync
 * The PDP carries two Judge.me app blocks: the product review widget and the store
 * "What Our Customers Say" carousel. Exactly one of the two is shown: the product's own reviews
 * once it has enough of them, the store carousel otherwise — and then the product widget's empty
 * state ("Be the first to write a review") is hidden with it, so the page never stacks two review
 * headings. "Enough" is the theme setting Product reviews → Minimum reviews to show the widget,
 * printed as data-reviews-min-count by sections/apps.liquid; Judge.me itself has no such threshold.
 * Roles come from snippets/app-block-reviews-role.liquid.
 */
(() => {
  // The section emits this script once per qualifying Apps section, and it is a classic script, so
  // a second copy would run the whole IIFE again with its own observers and timers.
  if (window.wtJudgemeReviewsSync) return;
  window.wtJudgemeReviewsSync = true;

  // In the theme editor the widgets paint sample reviews, so any verdict here would be fiction —
  // and hiding a block the merchant just selected leaves them looking at nothing.
  if (window.Shopify?.designMode === true) return;

  const GROUP_SELECTOR = ".wt-apps__wrapper";
  const BLOCK_SELECTOR = "[data-reviews-block]";
  const LOADER_SELECTOR = "[data-reviews-loader]";
  const PRODUCT_MARKER_SELECTOR = ".jdgm-rev-widg, .jdgm-review-widget, .jm-review-widget";
  // Judge.me prints nothing server-side. This marker says the app has finished with the block —
  // including the case where its empty state is set to render nothing at all.
  const READY_MARKER_SELECTOR = ".jdgm--done-setup-widget, .jdgm-rev-widg, .jm-review-widget, .jdgm-review-list";
  // The widget's visible body. Present but with no cards means it is mid-repaint, not empty.
  const WIDGET_BODY_SELECTOR = ".jdgm-rev-widg, .jm-review-widget, .jdgm-review-list";
  const REVIEW_ITEM_SELECTOR = ".jdgm-rev, .jm-review-item";
  // The widget's own "no reviews yet" state. Reading it is steadier than counting zero cards: the
  // revamp widget reports zero between two paints as well.
  const EMPTY_MARKER_SELECTOR = ".jm-no-reviews-state, .jdgm-empty-state, .jdgm-rev-widg--empty";
  const REVIEW_COUNT_ATTRIBUTE = "data-number-of-reviews";
  const MIN_COUNT_ATTRIBUTE = "reviewsMinCount";
  const SUMMARY_SELECTOR = ".jdgm-preview-badge, .jm-preview-badge, .jdgm-prev-badge";
  const CARD_SELECTOR = ".card, .athos-card, [data-product-card]";
  const DEFAULT_MIN_COUNT = 1;
  const OBSERVED_MUTATIONS = { childList: true, subtree: true };
  const ROLE_PRODUCT = "product";
  const ROLE_STORE = "store";
  const STATE_ATTRIBUTE = "data-reviews-state";
  const STATE_PENDING = "pending";
  const STATE_HAS_REVIEWS = "has";
  const STATE_NO_REVIEWS = "none";
  const LOADING_CLASS = "_loading";

  // Judge.me can defer painting until the widget scrolls into view. Past this point we stop waiting
  // and show the store carousel, which is the behaviour without this script.
  const RESOLVE_TIMEOUT = 6000;

  // Mutations arrive in bursts while the widget paints. A timer rather than requestAnimationFrame:
  // a tab that is never composited (background tab, embedded preview) never runs a frame, and the
  // pending frame would then swallow every later mutation.
  const SYNC_DEBOUNCE = 50;

  const isReady = (block) => block.querySelector(READY_MARKER_SELECTOR) !== null;
  const isEmptyState = (block) => block.querySelector(EMPTY_MARKER_SELECTOR) !== null;
  const hasWidgetBody = (block) => block.querySelector(WIDGET_BODY_SELECTOR) !== null;

  const roleOf = (block) => {
    if (block.querySelector(PRODUCT_MARKER_SELECTOR)) return ROLE_PRODUCT;
    return block.dataset.reviewsBlock;
  };

  const isInPageLink = (anchor) => anchor === null || anchor.getAttribute("href")?.startsWith("#") === true;

  const isMainProductSummary = (element, group) =>
    !group.contains(element) && !element.closest(CARD_SELECTOR) && isInPageLink(element.closest("a"));

  const declaredReviewCount = (productBlock, group) => {
    const counter =
      productBlock.querySelector(`[${REVIEW_COUNT_ATTRIBUTE}]`) ??
      [...document.querySelectorAll(`[${REVIEW_COUNT_ATTRIBUTE}]`)].find((element) =>
        isMainProductSummary(element, group)
      );
    if (!counter) return null;

    const declaredCount = parseInt(counter.getAttribute(REVIEW_COUNT_ATTRIBUTE), 10);
    return Number.isNaN(declaredCount) ? null : declaredCount;
  };

  const renderedReviewCount = (productBlock) => productBlock.querySelectorAll(REVIEW_ITEM_SELECTOR).length;

  const minReviewCount = (group) => {
    const configuredCount = parseInt(group.dataset[MIN_COUNT_ATTRIBUTE], 10);
    return Number.isNaN(configuredCount) ? DEFAULT_MIN_COUNT : configuredCount;
  };

  const createSummaryScrollRedirect = (storeBlock, group) => {
    const scrollToStoreBlock = () => {
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      storeBlock.setAttribute("tabindex", "-1");
      storeBlock.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
      storeBlock.focus({ preventScroll: true });
    };

    const handleClick = (event) => {
      const summary = event.target.closest?.(SUMMARY_SELECTOR);
      if (!summary || !isMainProductSummary(summary, group)) return;
      if (storeBlock.hidden) return;

      event.preventDefault();
      event.stopPropagation();
      scrollToStoreBlock();
    };

    document.addEventListener("click", handleClick, true);

    return () => document.removeEventListener("click", handleClick, true);
  };

  class ReviewsGroup {
    constructor(group) {
      this.group = group;
      this.minReviewCount = minReviewCount(group);
      this.blocks = Array.from(group.querySelectorAll(BLOCK_SELECTOR));
      this.scheduleSync = this.scheduleSync.bind(this);
      this.syncTimer = null;
      this.observer = null;
      this.timeout = null;
      this.removeSummaryRedirect = null;
      this.settled = false;
    }

    start() {
      this.sync();
      if (this.settled) return;

      this.observer = new MutationObserver(this.scheduleSync);
      this.observer.observe(this.group, OBSERVED_MUTATIONS);
      this.timeout = window.setTimeout(() => this.apply(false, true), RESOLVE_TIMEOUT);
    }

    stop() {
      this.observer?.disconnect();
      this.observer = null;

      if (this.syncTimer) window.clearTimeout(this.syncTimer);
      this.syncTimer = null;

      if (this.timeout) window.clearTimeout(this.timeout);
      this.timeout = null;
    }

    destroy() {
      this.stop();

      this.removeSummaryRedirect?.();
      this.removeSummaryRedirect = null;
    }

    scheduleSync() {
      if (this.syncTimer) return;

      this.syncTimer = window.setTimeout(() => {
        this.syncTimer = null;
        this.sync();
      }, SYNC_DEBOUNCE);
    }

    sync() {
      const productBlock = this.blocks.find((block) => roleOf(block) === ROLE_PRODUCT);
      const storeBlock = this.blocks.find((block) => roleOf(block) === ROLE_STORE);

      if (!storeBlock) return;

      this.storeBlock = storeBlock;

      if (storeBlock.getAttribute(STATE_ATTRIBUTE) === null) {
        // Hide first, decide after: the carousel must not flash on a product that has its own
        // reviews. The class hides the app block but not the loader, so the spinner stands in its
        // place — and it hides with visibility, so the carousel still measures itself correctly
        // while it initialises inside a block that may end up shown.
        storeBlock.setAttribute(STATE_ATTRIBUTE, STATE_PENDING);
        storeBlock.classList.add(LOADING_CLASS);
        storeBlock.querySelector(LOADER_SELECTOR)?.removeAttribute("hidden");
      }

      if (!productBlock) return;

      this.productBlock = productBlock;

      if (!isReady(productBlock)) return;

      const declaredCount = declaredReviewCount(productBlock, this.group);
      if (declaredCount !== null) {
        this.apply(declaredCount >= this.minReviewCount, true);
        return;
      }

      if (renderedReviewCount(productBlock) >= this.minReviewCount) {
        this.apply(true);
        return;
      }

      // Either the widget drew its empty state, or it drew nothing at all because the app is set to
      // hide it. A widget body with no cards is mid-repaint — wait for the next mutation.
      if (isEmptyState(productBlock) || !hasWidgetBody(productBlock)) this.apply(false);
    }

    apply(hasProductReviews, isFinal = false) {
      if (this.settled || !this.storeBlock) return;

      const state = hasProductReviews ? STATE_HAS_REVIEWS : STATE_NO_REVIEWS;

      if (this.storeBlock.getAttribute(STATE_ATTRIBUTE) !== state) {
        this.storeBlock.setAttribute(STATE_ATTRIBUTE, state);
        this.storeBlock.querySelector(LOADER_SELECTOR)?.setAttribute("hidden", "");
        this.storeBlock.classList.remove(LOADING_CLASS);
        this.storeBlock.hidden = hasProductReviews;
      }

      if (this.productBlock) {
        this.productBlock.hidden = !hasProductReviews && this.productWidgetWillShowSomething();

        if (this.productBlock.hidden && !this.removeSummaryRedirect) {
          this.removeSummaryRedirect = createSummaryScrollRedirect(this.storeBlock, this.group);
        }
      }

      // "Enough reviews" is final, and so is a count the widget published itself. "Not enough" read
      // off the DOM is final only once the widget finished its own painting — it can report zero
      // between two paints, and settling there would leave that state on screen with no observer
      // left to correct it.
      if (hasProductReviews || isFinal || this.isProductWidgetDone()) {
        this.settled = true;
        this.stop();
      }
    }

    productWidgetWillShowSomething() {
      const declaredCount = declaredReviewCount(this.productBlock, this.group);
      if (declaredCount > 0) return true;

      return isEmptyState(this.productBlock) || hasWidgetBody(this.productBlock);
    }

    isProductWidgetDone() {
      if (!this.productBlock || !isReady(this.productBlock)) return false;

      return isEmptyState(this.productBlock) || !hasWidgetBody(this.productBlock);
    }
  }

  const groups = new Map();

  const initGroups = (root) => {
    root.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
      if (groups.has(group)) return;
      if (group.querySelectorAll(BLOCK_SELECTOR).length < 2) return;

      const reviewsGroup = new ReviewsGroup(group);
      groups.set(group, reviewsGroup);
      reviewsGroup.start();
    });
  };

  const destroyGroups = (root) => {
    groups.forEach((reviewsGroup, group) => {
      if (!root.contains(group)) return;

      reviewsGroup.destroy();
      groups.delete(group);
    });
  };

  initGroups(document);

  // The theme editor re-renders a section into a fresh DOM: the new wrappers need their own group,
  // and the old one's observer would otherwise keep watching a detached node.
  document.addEventListener("shopify:section:load", (event) => initGroups(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroyGroups(event.target));
})();
